import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Sparkles, CheckCircle2, X } from 'lucide-react';
import { MealCard } from './MealCard';
import { EmptyMealSlot } from './EmptyMealSlot';
import { WeekMenu } from './WeekMenu';
import { RecipeDetail } from './RecipeDetail';
import { SwapMealModal } from './SwapMealModal';
import { AddMealModal } from './AddMealModal';
import BottomNav from './BottomNav';
import { FullWeekView } from './FullWeekView';
import { ManualRecipeForm } from './ManualRecipeForm';
import { ConfirmationDrawer } from './ConfirmationDrawer';
import { AddToMealPlanDrawer } from './AddToMealPlanDrawer';
import { ReplaceMealConfirmDrawer } from './ReplaceMealConfirmDrawer';
import {
  getStartOfWeek,
  getWeekStartISO,
  formatWeekLabel,
  getCurrentDayShortName,
  addWeeks
} from '../utils/dateUtils';
import { apiGet, apiPost, apiPatch, apiDelete } from '../utils/apiClient';
import { readViewWeekOffset, writeViewWeekOffset } from '../utils/viewWeek';
import { isObjectId, deriveImageUrl, deriveImageThumbUrl } from '../utils/recipeHelpers';
import { normalizeWeeklyPlanRecipe as normalizeRecipeDetail } from '../utils/normalizeWeeklyPlanRecipe';
import { useRecipeActions } from '../hooks/useRecipeActions';

// Generate initial weeks data with real dates
const generateInitialWeeks = (numWeeks = 12) => {
  const today = new Date();
  const currentWeekStart = getStartOfWeek(today);

  const weeks = [];

  // Generate 2 past weeks, current week, and future weeks
  const pastWeeks = 2;

  for (let i = -pastWeeks; i < numWeeks; i++) {
    const weekStart = addWeeks(currentWeekStart, i);
    const label = formatWeekLabel(weekStart);

    // Do not pre-populate mock meals; hydrate from backend
    const isFirstWeek = false;

    weeks.push({
      label,
      days: {
        Mon: isFirstWeek ? [
          {
            id: '1',
            name: 'Avocado Omelette',
            image: 'https://images.unsplash.com/photo-1642689690565-bf0afb7eb41e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxhdm9jYWRvJTIwb21lbGV0dGUlMjBicmVha2Zhc3R8ZW58MXx8fHwxNzY1MDgzNzQzfDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
            tags: ['Low FODMAP', 'GF', 'Veg'],
            mealType: 'Breakfast',
            serves: 2
          },
          {
            id: '2',
            name: 'Greek Salad',
            image: 'https://images.unsplash.com/photo-1563046937-1f82e1a115cd?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxncmVlayUyMHNhbGFkJTIwaGVhbHRoeXxlbnwxfHx8fDE3NjUwODM3NDN8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
            tags: ['Low FODMAP', 'GF', 'Veg'],
            mealType: 'Lunch',
            serves: 4
          },
          {
            id: '3',
            name: 'Vegetable Stir Fry',
            image: 'https://images.unsplash.com/photo-1464500650248-1a4b45debb9f?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx2ZWdldGFibGUlMjBzdGlyJTIwZnJ5fGVufDF8fHx8MTc2NTA1MjgxNHww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
            tags: ['Low FODMAP', 'GF', 'Veg'],
            mealType: 'Dinner',
            serves: 3
          }
        ] : [],
        Tue: isFirstWeek ? [
          {
            id: '4',
            name: 'Chia Pudding',
            image: 'https://images.unsplash.com/photo-1552528352-59648b345866?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjaGlhJTIwcHVkZGluZyUyMGJyZWFrZmFzdHxlbnwxfHx8fDE3NjUwODM3NDR8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
            tags: ['GF', 'Vegan'],
            mealType: 'Breakfast',
            serves: 1
          },
          {
            id: '5',
            name: 'Greek Salad',
            image: 'https://images.unsplash.com/photo-1563046937-1f82e1a115cd?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxncmVlayUyMHNhbGFkJTIwaGVhbHRoeXxlbnwxfHx8fDE3NjUwODM3NDN8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
            tags: ['Low FODMAP', 'GF', 'Veg'],
            mealType: 'Lunch',
            serves: 4
          },
          {
            id: '6',
            name: 'Vegetable Stir Fry',
            image: 'https://images.unsplash.com/photo-1464500650248-1a4b45debb9f?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx2ZWdldGFibGUlMjBzdGlyJTIwZnJ5fGVufDF8fHx8MTc2NTA1MjgxNHww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
            tags: ['Low FODMAP', 'GF', 'Veg'],
            mealType: 'Dinner',
            serves: 3
          }
        ] : [],
        Wed: [],
        Thu: [],
        Fri: [],
        Sat: [],
        Sun: []
      }
    });
  }

  return weeks;
};


// Legacy mock recipe data removed; backend weeklyPlans now drive content.
/* const mockRecipeDetails = {
  '1': {
    id: '1',
    name: 'Avocado Omelette',
    image: 'https://images.unsplash.com/photo-1642689690565-bf0afb7eb41e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxhdm9jYWRvJTIwb21lbGV0dGUlMjBicmVha2Zhc3R8ZW58MXx8fHwxNzY1MDgzNzQzfDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
    prepTime: 5,
    cookTime: 8,
    mealType: 'breakfast',
    tags: ['Low FODMAP', 'GF', 'Veg'],
    servings: 2,
    ingredients: [
      { name: 'Eggs', quantity: '4 large' },
      { name: 'Ripe avocado', quantity: '1 large' },
      { name: 'Butter', quantity: '1 tbsp' },
      { name: 'Salt', quantity: '1/4 tsp' },
      { name: 'Black pepper', quantity: 'to taste' },
      { name: 'Cherry tomatoes', quantity: '6' },
    ],
    method: [
      'Beat the eggs in a bowl with salt and pepper.',
      'Melt butter in a non-stick pan over medium heat.',
      'Pour in the eggs and cook gently, stirring occasionally.',
      'When eggs are nearly set, add sliced avocado to one half.',
      'Fold omelette in half and cook for another minute.',
      'Serve with halved cherry tomatoes on the side.',
    ],
    nutrition: {
      calories: 320,
      protein: 16,
      carbs: 12,
      fat: 24,
    },
    unitSystem: 'metric',
  },
  '2': {
    id: '2',
    name: 'Greek Salad',
    image: 'https://images.unsplash.com/photo-1563046937-1f82e1a115cd?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxncmVlayUyMHNhbGFkJTIwaGVhbHRoeXxlbnwxfHx8fDE3NjUwODM3NDN8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
    prepTime: 15,
    cookTime: 0,
    mealType: 'lunch',
    tags: ['Low FODMAP', 'GF', 'Veg'],
    servings: 4,
    ingredients: [
      { name: 'Cucumber', quantity: '1 large' },
      { name: 'Cherry tomatoes', quantity: '250g' },
      { name: 'Red onion', quantity: '1/2' },
      { name: 'Feta cheese', quantity: '150g' },
      { name: 'Kalamata olives', quantity: '100g' },
      { name: 'Olive oil', quantity: '3 tbsp' },
      { name: 'Red wine vinegar', quantity: '1 tbsp' },
      { name: 'Dried oregano', quantity: '1 tsp' },
    ],
    method: [
      'Chop cucumber and tomatoes into bite-sized pieces.',
      'Thinly slice the red onion.',
      'Combine vegetables in a large bowl with olives.',
      'Crumble feta cheese over the top.',
      'Drizzle with olive oil and vinegar.',
      'Sprinkle with oregano and toss gently.',
      'Let stand for 5 minutes before serving.',
    ],
    nutrition: {
      calories: 280,
      protein: 10,
      carbs: 14,
      fat: 20,
    },
    unitSystem: 'metric',
  },
  '3': {
    id: '3',
    name: 'Vegetable Stir Fry',
    image: 'https://images.unsplash.com/photo-1464500650248-1a4b45debb9f?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHx2ZWdldGFibGUlMjBzdGlyJTIwZnJ5fGVufDF8fHx8MTc2NTA1MjgxNHww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral',
    prepTime: 10,
    cookTime: 12,
    mealType: 'dinner',
    tags: ['Low FODMAP', 'GF', 'Veg'],
    servings: 3,
    ingredients: [
      { name: 'Bell peppers (mixed)', quantity: '2 large' },
      { name: 'Broccoli florets', quantity: '200g' },
      { name: 'Carrots', quantity: '2 medium' },
      { name: 'Snow peas', quantity: '150g' },
      { name: 'Soy sauce', quantity: '3 tbsp' },
      { name: 'Sesame oil', quantity: '2 tbsp' },
      { name: 'Garlic', quantity: '3 cloves' },
      { name: 'Ginger', quantity: '1 inch piece' },
      { name: 'Rice or noodles', quantity: 'to serve' },
    ],
    method: [
      'Slice all vegetables into similar-sized pieces.',
      'Mince garlic and grate ginger.',
      'Heat sesame oil in a large wok over high heat.',
      'Add garlic and ginger, stir-fry for 30 seconds.',
      'Add harder vegetables first (carrots, broccoli) and stir-fry for 3 minutes.',
      'Add remaining vegetables and continue stir-frying for 3-4 minutes.',
      'Pour in soy sauce and toss everything together.',
      'Serve immediately over rice or noodles.',
    ],
    nutrition: {
      calories: 180,
      protein: 8,
      carbs: 22,
      fat: 8,
    },
    unitSystem: 'metric',
  },
}; */



export default function MealPlan({ userId = 'user-default' }) {
  //return (
    //<div className="min-h-screen bg-[#F8F7F5]">
      
      // Current week is at index 2 (2 past weeks precede it). Restore the shared "viewed
      // week" so navigating here from Shopping List lands on the same week; size weeksData to
      // cover a restored future selection.
      const [weeksData, setWeeksData] = useState(() => {
        const o = readViewWeekOffset();
        return generateInitialWeeks(o && o > 0 ? Math.max(12, o + 2) : 12);
      });
      const [currentWeekIndex, setCurrentWeekIndex] = useState(() => {
        const o = readViewWeekOffset();
        return o === null ? 2 : Math.max(0, 2 + o);
      });
      const [selectedDay, setSelectedDay] = useState(getCurrentDayShortName());
      // Store the index of the actual current week (today's week) - updates when we add past weeks
      const [actualCurrentWeekIndex] = useState(2);
      const [viewingMealRecipe, setViewingMealRecipe] = useState(null);
      const [swapMealModalOpen, setSwapMealModalOpen] = useState(false);
      const [mealToSwap, setMealToSwap] = useState(null);
      const [addMealModalOpen, setAddMealModalOpen] = useState(false);
      const [selectedMealType, setSelectedMealType] = useState(null);
      // Inline error box (no toasts). Stays until dismissed or replaced by the next error.
  const [pageError, setPageError] = useState('');
  const [isFullWeekView, setIsFullWeekView] = useState(false);
      const [isLargeScreen, setIsLargeScreen] = useState(false);
      // Recipe edit/delete/add-to-plan drawer state + handlers live in useRecipeActions (below).
      const [isGenerating, setIsGenerating] = useState(false);
      const [, setGenError] = useState(null);
      const [isLoadingWeek, setIsLoadingWeek] = useState(false);
      const [hydratedWeeks, setHydratedWeeks] = useState({});
      const [successBannerMessage, setSuccessBannerMessage] = useState('');
      const successBannerTimerRef = useRef(null);
      // 0=Sun..6=Sat; default Monday until user preference loads
      // 0=Sun..6=Sat. Seed from the cached preference so week dates render with the user's
      // week start immediately on (re)load, avoiding a Monday-dates flash before
      // /user/preferences resolves; default Monday only when nothing is cached yet.
      const [weekStartDow, setWeekStartDow] = useState(() => {
        try {
          const raw = localStorage.getItem('mp:weekStartDow');
          if (raw === null) return 1;
          const n = Number(raw);
          return Number.isInteger(n) && n >= 0 && n <= 6 ? n : 1;
        } catch {
          return 1;
        }
      });
      const days = useMemo(() => {
        const order = [];
        for (let i = 0; i < 7; i++) {
          order.push(['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][(weekStartDow + i) % 7]);
        }
        return order;
      }, [weekStartDow]);

      const currentWeek = weeksData[currentWeekIndex];
      const currentMeals = currentWeek.days[selectedDay] || [];

      // Check if we're on the actual current week (today's week)
      const isCurrentWeek = currentWeekIndex === actualCurrentWeekIndex;
      const todayShortName = getCurrentDayShortName();

      // Organize meals by meal type for consistent display
      const mealsByType = {
        Breakfast: currentMeals.find(m => m.mealType === 'Breakfast'),
    Lunch: currentMeals.find(m => m.mealType === 'Lunch'),
    Dinner: currentMeals.find(m => m.mealType === 'Dinner'),
  };

  // Check if entire week has any meals
  const weekHasAnyMeals = () => {
    return days.some(day => currentWeek.days[day]?.length > 0);
  };

  const handlePreviousWeek = () => {
    // Prevent going back more than 2 weeks from current week
    // currentWeekIndex 0 is 2 weeks ago (actualCurrentWeekIndex is 2)
    if (currentWeekIndex === 0) {
      // Already at the maximum past limit (2 weeks ago)
      return;
    }

      const newWeekIndex = currentWeekIndex - 1;
      setCurrentWeekIndex(newWeekIndex);

      // If moving to the actual current week, select today's day
      // Otherwise, select the first day of the configured week
      if (newWeekIndex === actualCurrentWeekIndex) {
        setSelectedDay(getCurrentDayShortName());
    } else {
        setSelectedDay(days[0]);
    }
  };

  const handleNextWeek = () => {
    // If we're at the last week, generate more weeks
    if (currentWeekIndex >= weeksData.length - 1) {
        setWeeksData(prevWeeks => {
          const newWeeks = [...prevWeeks];

          // Generate 4 more weeks
          for (let i = 1; i <= 4; i++) {
            const today = new Date();
            const currentWeekStart = getStartOfWeek(today);
            // Calculate offset from current week (actualCurrentWeekIndex is at position 2 initially)
            const weeksFromToday = prevWeeks.length - actualCurrentWeekIndex + i - 1;
            const weekStart = addWeeks(currentWeekStart, weeksFromToday);
            const label = formatWeekLabel(weekStart);

            newWeeks.push({
              label,
              days: {
                Mon: [],
                Tue: [],
                Wed: [],
                Thu: [],
                Fri: [],
                Sat: [],
                Sun: []
              }
            });
          }

          return newWeeks;
        });
    }

      const newWeekIndex = currentWeekIndex + 1;
      setCurrentWeekIndex(newWeekIndex);

      // If moving to the actual current week, select today's day
      // Otherwise, select the first day of the configured week
      if (newWeekIndex === actualCurrentWeekIndex) {
        setSelectedDay(getCurrentDayShortName());
    } else {
        setSelectedDay(days[0]);
    }
  };

  const handleDeleteWeek = async () => {
    console.log('Delete week meal plan');

    // Optimistically clear all meals for the current week in local state
    setWeeksData(prevWeeks => {
      const newWeeks = [...prevWeeks];
      newWeeks[currentWeekIndex] = {
        ...newWeeks[currentWeekIndex],
        days: {
          Mon: [],
          Tue: [],
          Wed: [],
          Thu: [],
          Fri: [],
          Sat: [],
          Sun: [],
        },
      };
      return newWeeks;
    });

    // Clear this week's meals and auto-generated shopping list items on the server,
    // preserving any user-added/edited shopping list entries.
    try {
      await apiDelete(`/weeklyPlans/week-start/${encodeURIComponent(currentWeekStartISO)}`);
    } catch (e) {
      console.warn('Failed to clear week on backend:', e);
    }
  };

  const handleViewFullWeek = () => {
        setIsFullWeekView(true);
  };

  const handleServesChange = async (id, serves) => {
    // Optimistic UI update
    setWeeksData(prevWeeks => {
      const newWeeks = [...prevWeeks];
      const weekToUpdate = { ...newWeeks[currentWeekIndex] };
      Object.keys(weekToUpdate.days).forEach(day => {
        const dayMeals = [...(weekToUpdate.days[day] || [])];
        const idx = dayMeals.findIndex(m => m.id === id);
        if (idx !== -1) {
          dayMeals[idx] = { ...dayMeals[idx], serves };
          weekToUpdate.days = { ...weekToUpdate.days, [day]: dayMeals };
        }
      });
      newWeeks[currentWeekIndex] = weekToUpdate;
      return newWeeks;
    });
    // Persist to weeklyPlans if this is a real weekly plan doc
    if (isObjectId(id)) {
      try {
        await apiPatch(`/weeklyPlans/${encodeURIComponent(id)}`, { serves });
        await regenerateShoppingListForCurrentWeek();
      } catch (e) {
        console.warn('Failed to persist serves:', e);
        setPageError("We couldn't save the new number of serves. Please try again.");
      }
    }
  };

  const handleRecipeServesChange = async (serves) => {
    const rid = viewingMealRecipe?.id;
    if (!rid) return;
    // Update the viewing recipe detail locally
    setViewingMealRecipe(prev => prev ? { ...prev, servings: serves } : prev);
    // Update the meal card in the current week's data
    setWeeksData(prevWeeks => {
      const newWeeks = [...prevWeeks];
      const weekToUpdate = { ...newWeeks[currentWeekIndex] };
      for (const day of Object.keys(weekToUpdate.days)) {
        const meals = [...(weekToUpdate.days[day] || [])];
        const idx = meals.findIndex(m => m.id === rid);
        if (idx !== -1) {
          meals[idx] = { ...meals[idx], serves: serves };
          weekToUpdate.days = { ...weekToUpdate.days, [day]: meals };
        }
      }
      newWeeks[currentWeekIndex] = weekToUpdate;
      return newWeeks;
    });
    // Persist for real weeklyPlans documents
    if (isObjectId(rid)) {
      try {
        await apiPatch(`/weeklyPlans/${encodeURIComponent(rid)}`, { serves });
        await regenerateShoppingListForCurrentWeek();
      } catch (e) {
        console.warn('Failed to persist serves (detail):', e);
        setPageError("We couldn't save the new number of serves. Please try again.");
      }
    }
  };

  const handleSwap = (id) => {
    // Search across all days in the current week (not just selected day)
    const meal = Object.values(currentWeek.days)
      .flat()
      .find(m => m.id === id);
      if (meal) {
        setMealToSwap(meal);
      setSwapMealModalOpen(true);
    }
  };

  const handleRemove = async (id) => {
    // Optimistically remove the meal from the local week view
    setWeeksData(prevWeeks => {
      const newWeeks = [...prevWeeks];
      const weekToUpdate = { ...newWeeks[currentWeekIndex] };
      const dayMeals = [...(weekToUpdate.days[selectedDay] || [])];

      const filteredMeals = dayMeals.filter(m => m.id !== id);

      weekToUpdate.days = {
        ...weekToUpdate.days,
        [selectedDay]: filteredMeals,
      };

      newWeeks[currentWeekIndex] = weekToUpdate;
      return newWeeks;
    });

    // If this is a persisted weekly plan entry, delete it server-side
    if (isObjectId(id)) {
      try {
        await apiDelete(`/weeklyPlans/${encodeURIComponent(id)}`);
        // Regenerate shopping list for this week so quantities reflect the removal
        await regenerateShoppingListForCurrentWeek();
      } catch (e) {
        console.warn('Failed to delete weekly plan entry or regenerate list:', e);
      }
    }
  };

  const handleViewDetails = async (id) => {
    // Find the meal across the entire current week (not just selected day)
    const meal =
      Object.values(currentWeek.days)
        .flat()
        .find((m) => m.id === id) || currentMeals.find((m) => m.id === id);
    if (!meal) {
      setPageError("We couldn't find that meal in this week. Please refresh and try again.");
      return;
    }
    const weeklyPlanId = meal.id;
    const recipeId = meal.recipeId;
    const isLocalOnly = meal.isLocalOnly === true;
    try {
      // For local-only additions (from All Recipes without persistence), fetch recipe directly
      if (isLocalOnly && isObjectId(String(recipeId || weeklyPlanId))) {
        const raw = await apiGet(`/recipes/${encodeURIComponent(String(recipeId || weeklyPlanId))}`);
        const recipe = normalizeRecipeDetail(raw);
        setViewingMealRecipe(recipe);
        return;
      }
      // If this slot was locally swapped to a different base recipe,
      // show the swapped recipe details while keeping the weeklyPlan id for serves persistence.
      if (meal.isLocalSwap && isObjectId(String(recipeId))) {
        try {
          const baseRaw = await apiGet(`/recipes/${encodeURIComponent(String(recipeId))}`);
          const base = normalizeRecipeDetail(baseRaw);
          const baseServings = Number(base.servings) || 1;
          const servesForWeek = Number(meal.serves) || baseServings;
          setViewingMealRecipe({ ...base, id: weeklyPlanId, weeklyPlanId, servings: servesForWeek, baseServings });
          return;
        } catch (e) {
          console.warn('Failed to fetch swapped base recipe; falling back to weekly plan doc:', e);
        }
      }
      if (isObjectId(weeklyPlanId)) {
        // Preferred: weeklyPlans doc reflects per-week changes (serves, etc.)
        const raw = await apiGet(`/weeklyPlans/${encodeURIComponent(weeklyPlanId)}`);
        const recipe = normalizeRecipeDetail(raw);
        // Always load the base recipe for authoritative method/servings when recipeId exists
        let baseServings = recipe.servings;
        let method = recipe.method;
        if (isObjectId(String(recipeId))) {
          try {
            const baseRaw = await apiGet(`/recipes/${encodeURIComponent(String(recipeId))}`);
            const base = normalizeRecipeDetail(baseRaw);
            baseServings = Number(base.servings) || baseServings;
            if (Array.isArray(base.method) && base.method.length) {
              method = base.method; // prefer canonical method from Recipes collection
            }
          } catch (e) {
            console.warn('Failed to fetch base recipe for baseline servings/method:', e);
          }
        }
        setViewingMealRecipe({ ...recipe, weeklyPlanId, method, baseServings });
        return;
      }
      // Fallback for non-persisted/mock items
      if (isObjectId(String(recipeId))) {
        const raw = await apiGet(`/recipes/${encodeURIComponent(String(recipeId))}`);
        const recipe = normalizeRecipeDetail(raw);
        setViewingMealRecipe(recipe);
        return;
      }
      setPageError("This meal's recipe isn't in your recipes, so it can't be opened. Remove the meal and add another.");
    } catch (e) {
      console.warn('Failed to load recipe details:', e);
      setPageError("We couldn't open this recipe just now. Please try again.");
    }
  };

  const handleAddMeal = (mealType) => {
        console.log(`Add meal for ${mealType} on ${selectedDay}`);
      setSelectedMealType(mealType);
      setAddMealModalOpen(true);
  };

  const handleAddMealFromModal = async (recipe) => {
    if (!selectedMealType || !recipe) return;

    const recipeId = String(recipe.id || '');

    // Map short day label (Mon...Sun) to full weekday name
    const dayShortToFull = {
      Mon: 'Monday',
      Tue: 'Tuesday',
      Wed: 'Wednesday',
      Thu: 'Thursday',
      Fri: 'Friday',
      Sat: 'Saturday',
      Sun: 'Sunday',
    };
    const assignedDay = dayShortToFull[selectedDay] || 'Monday';

    // Compute the assigned date ISO (YYYY-MM-DD) for this slot within the current week
    let assignedDateISO = currentWeekStartISO;
    try {
      const start = new Date(currentWeekStartISO + 'T00:00:00Z');
      const dayIndex = days.indexOf(selectedDay);
      if (dayIndex > 0) {
        start.setUTCDate(start.getUTCDate() + dayIndex);
      }
      assignedDateISO = start.toISOString().slice(0, 10);
    } catch {
      assignedDateISO = currentWeekStartISO;
    }

    const mealTypeLower = (selectedMealType || recipe.mealType || '').toLowerCase();
    let assignedMealNumber = 3; // default dinner
    if (mealTypeLower === 'breakfast') assignedMealNumber = 1;
    else if (mealTypeLower === 'lunch') assignedMealNumber = 2;

    const preferredServes = getPreferredServes(mealTypeLower, recipe.servings ?? recipe.serves);

    let newMeal = null;

    // If this is a real backend recipe, create a weeklyPlans entry and regenerate shopping list
    if (isObjectId(recipeId)) {
      try {
        const created = await apiPost('/weeklyPlans', {
          recipeId,
          weekStartDate: currentWeekStartISO,
          assignedDate: assignedDateISO,
          assignedDay,
          assignedMealNumber,
        });

        const doc = created || {};
        const id = String(doc._id || recipeId);
        const image = deriveImageThumbUrl(doc) || deriveImageThumbUrl(recipe) || deriveImageUrl(doc) || recipe.image;
        const servesFromDoc = Number(doc.serves ?? doc.servings ?? recipe.servings ?? recipe.serves) || 1;
        const tags = Array.isArray(doc.tags) ? doc.tags : (Array.isArray(recipe.tags) ? recipe.tags : []);
        const mealTypeCap =
          typeof doc.mealType === 'string' && doc.mealType.length
            ? doc.mealType.charAt(0).toUpperCase() + doc.mealType.slice(1).toLowerCase()
            : selectedMealType;

        if (preferredServes !== servesFromDoc && isObjectId(id)) {
          try {
            await apiPatch(`/weeklyPlans/${encodeURIComponent(id)}`, { serves: preferredServes });
          } catch (e) {
            console.warn('Failed to apply preferred serves on add:', e);
          }
        }

        newMeal = {
          id,
          name: doc.name || recipe.name,
          image,
          tags,
          mealType: mealTypeCap,
          serves: preferredServes || servesFromDoc,
          recipeId: doc.recipeId ? String(doc.recipeId) : recipeId,
        };

        // Maintain week-level recipe details map for this week
        if (doc.recipeId || isObjectId(recipeId)) {
          const key = String(doc.recipeId || recipeId);
          setWeekRecipeDetailsMap(prev => ({
            ...prev,
            [key]: {
              servings: preferredServes || servesFromDoc,
              ingredients: Array.isArray(doc.ingredients) ? doc.ingredients : [],
            },
          }));
        }

        try {
          await regenerateShoppingListForCurrentWeek();
        } catch (e) {
          console.warn('Failed to regenerate shopping list after add:', e);
        }
      } catch (e) {
        console.warn('Failed to create weekly plan entry for added meal:', e);
        // Thrown back to the Add meal pop-up, which shows it inline and stays open.
        throw new Error("We couldn't add this meal just now. Please try again.");
      }
    }

    // Never add a meal that isn't saved. This used to add sample/demo recipes (and failed saves)
    // to the screen only: they showed in the slot, couldn't open ("Unable to open this meal's
    // recipe"), and vanished on refresh. Bug reported in QA 2026-10-11.
    if (!newMeal) {
      throw new Error("That recipe isn't in your recipes, so it can't be added. Please choose another.");
    }

    // Add the meal to the weeksData state
    setWeeksData(prevWeeks => {
      const newWeeks = [...prevWeeks];
      const weekToUpdate = { ...newWeeks[currentWeekIndex] };
      const dayMeals = [...(weekToUpdate.days[selectedDay] || [])];

      dayMeals.push(newMeal);

      weekToUpdate.days = {
        ...weekToUpdate.days,
        [selectedDay]: dayMeals,
      };

      newWeeks[currentWeekIndex] = weekToUpdate;
      return newWeeks;
    });

    setAddMealModalOpen(false);
    setSelectedMealType(null);
  };

  const handleAutoGenerateAddMeal = () => {
        console.log(`Auto generate new ${selectedMealType} meal`);
    // In production: Call API to generate a new meal suggestion
    // fetch(`/api/weekly-plan/generate`, {
        //   method: 'POST',
        //   headers: { 'Content-Type': 'application/json' },
        //   body: JSON.stringify({ 
        //     mealType: selectedMealType, 
        //     day: selectedDay,
        //     weekLabel: currentWeek.label 
        //   })
        // })
        setAddMealModalOpen(false);
      setSelectedMealType(null);
  };

  

  // Map ISO date to 'Mon'...'Sun' relative to the given weekStart ISO
  const dayShortFromISO = (weekStartISO, assignedDateISO) => {
    try {
      const start = new Date(weekStartISO + 'T00:00:00Z');
      const assigned = new Date(assignedDateISO + 'T00:00:00Z');
      const diffMs = assigned - start;
      const diffDays = Math.max(0, Math.min(6, Math.floor(diffMs / (1000 * 60 * 60 * 24))));
      return days[diffDays];
    } catch {
      return 'Mon';
    }
  };

  const capitalize = (s) =>
    typeof s === 'string' && s.length ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s;


   // Backend-driven generate for the currently viewed week
   const handleGenerateWeek = async () => {
    setIsGenerating(true);
    setGenError(null);
    try {
      // Figure out which week the user is viewing
      const weeksOffset = currentWeekIndex - actualCurrentWeekIndex;
      const base = new Date();
      base.setDate(base.getDate() + weeksOffset * 7);
      const weekStartDate = getWeekStartISO(base, weekStartDow); // YYYY-MM-DD using user week start

      // 1) Generate and persist weekly plan for that week
      await apiGet(`/generate-weekly-plan?weekStartDate=${encodeURIComponent(weekStartDate)}`);

      // 2) Generate shopping list (then refresh the Shopping List tab)
      try {
        await apiPost(
          `/generate-shopping-list/week-start/${encodeURIComponent(weekStartDate)}`,
          {}
        );
      } catch (e) {
        console.warn('Shopping list generation failed:', e);
      }

      // 3) Fetch the saved weekly plan
      const plan = await apiGet(
        `/weeklyPlans/week-start/${encodeURIComponent(weekStartDate)}`
      );
      const rawMeals = Array.isArray(plan?.meals) ? plan.meals : (Array.isArray(plan) ? plan : []);

      // 4a) Build a recipe details map for the shopping list (ingredients + servings)
      const detailsMap = {};
      for (const raw of rawMeals) {
        const rec = normalizeRecipeDetail(raw);
        const key = raw.recipeId || raw._id || rec.id;
        if (key) {
          detailsMap[key] = {
            servings: rec.servings,
            ingredients: rec.ingredients
          };
        }
      }
      setWeekRecipeDetailsMap(detailsMap);

      // 4b) Normalize into local days structure used by this UI
      const normalizedDays = { Mon: [], Tue: [], Wed: [], Thu: [], Fri: [], Sat: [], Sun: [] };
      for (const m of rawMeals) {
        const mealType = capitalize(m.mealType || m.assignedMealType || 'Dinner');
        const name = m.name || m.recipe?.name || 'Untitled';
        const image = deriveImageThumbUrl(m) || deriveImageThumbUrl(m.recipe) || deriveImageUrl(m) || deriveImageUrl(m.recipe) || '';
        const serves = m.serves || m.servings || 2;
        const id = m.id || m._id || m.recipeId || `${mealType}-${name}`;
        const assignedDateISO = m.assignedDate || m.date;
        const dayShort = assignedDateISO
          ? dayShortFromISO(weekStartDate, assignedDateISO)
          : m.assignedDay || 'Mon';
        if (normalizedDays[dayShort]) {
          normalizedDays[dayShort].push({
            id: id,
            name,

            image,
            tags: m.tags || [],
            mealType,
            serves,
            recipeId: m.recipeId,
          });
        }
      }

      // 5) Update the current week with backend-generated meals
      setWeeksData(prev => {
        const next = [...prev];
        next[currentWeekIndex] = { ...next[currentWeekIndex], days: normalizedDays };
        return next;
      });
    } catch (err) {
      console.error('Failed to generate weekly plan:', err);
      setGenError(err?.message || 'Failed to generate weekly plan');
      setPageError("We couldn't generate your meal plan just now. Please try again.");
    } finally {
      setIsGenerating(false);
    }
 };
 
 // Load existing weekly plan for the current view (on mount and when week changes)
 useEffect(() => {
   let cancelled = false;
   (async () => {
     try {
       setIsLoadingWeek(true);
       // Determine ISO week start for the currently viewed week
       const weeksOffset = currentWeekIndex - actualCurrentWeekIndex;
       const base = new Date();
       base.setDate(base.getDate() + weeksOffset * 7);
      const weekStartDate = getWeekStartISO(base, weekStartDow); // YYYY-MM-DD using user week start

       // Fetch saved weekly plan for this week
       const plan = await apiGet(`/weeklyPlans/week-start/${encodeURIComponent(weekStartDate)}`);
       const rawMeals = Array.isArray(plan?.meals) ? plan.meals : (Array.isArray(plan) ? plan : []);
       if (!rawMeals.length) return; // nothing to hydrate

       // Build a recipe details map for this week (ingredients + servings)
       const detailsMap = {};
       for (const raw of rawMeals) {
         const rec = normalizeRecipeDetail(raw);
         const key = raw.recipeId || raw._id || rec.id;
         if (key) {
           detailsMap[key] = {
             servings: rec.servings,
             ingredients: rec.ingredients
           };
         }
       }
       setWeekRecipeDetailsMap(detailsMap);

       // Normalize into local days structure
       const normalizedDays = { Mon: [], Tue: [], Wed: [], Thu: [], Fri: [], Sat: [], Sun: [] };
       for (const m of rawMeals) {
         const mealType =
            typeof m.mealType === 'string' && m.mealType.length
              ? m.mealType.charAt(0).toUpperCase() + m.mealType.slice(1).toLowerCase()
              : (typeof m.assignedMealType === 'string' ? m.assignedMealType : 'Dinner');
          const name = m.name || m.recipe?.name || 'Untitled';
          const image = deriveImageThumbUrl(m) || deriveImageThumbUrl(m.recipe) || deriveImageUrl(m) || deriveImageUrl(m.recipe) || '';
          const serves = m.serves || m.servings || 2;
          const id = m.id || m._id || m.recipeId || `${mealType}-${name}`;
          const assignedDateISO = m.assignedDate || m.date;
          const dayShort = assignedDateISO
            ? dayShortFromISO(weekStartDate, assignedDateISO)
            : (m.assignedDay || 'Mon');
          if (normalizedDays[dayShort]) {
            normalizedDays[dayShort].push({ id, name, image, tags: m.tags || [], mealType, serves, recipeId: m.recipeId });
          }
        }

        if (!cancelled) {
          setWeeksData(prev => {
            const next = [...prev];
            next[currentWeekIndex] = { ...next[currentWeekIndex], days: normalizedDays };
            return next;
          });
        }
      } catch (e) {
        console.warn('Load weekly plan failed:', e);
      }
      finally {
      if (!cancelled) {
        setIsLoadingWeek(false);
        setHydratedWeeks(prev => ({ ...prev, [currentWeekIndex]: true }));
      }
     }
   })();
   return () => { cancelled = true; };
 // We intentionally only key this hydration effect off the
 // viewed week index and weekStartDow; helper functions used
 // inside (dayShortFromISO, normalizeRecipeDetail) are pure.
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [currentWeekIndex, actualCurrentWeekIndex, weekStartDow]);

  // Swap Meal Modal: commit a selected swap option into the plan
  const handleSwapMeal = async (recipeId) => {
    if (!mealToSwap) return;

    // Fetch the selected recipe from backend
    let selected;
    try {
      const raw = await apiGet(`/recipes/${encodeURIComponent(String(recipeId))}`);
      const detail = normalizeRecipeDetail(raw);
      // Normalize minimal fields inline (image extraction matches Recipes.jsx)
      const image =
        (typeof raw.image === 'string' && raw.image) ||
        raw.image?.src || raw.image?.url ||
        (Array.isArray(raw.imageUrls) && raw.imageUrls[0]) ||
        raw.image_url || raw.photoUrl || '';
      const imageThumb =
        (typeof raw.imageThumb === 'string' && raw.imageThumb) ||
        (typeof raw.thumbnail === 'string' && raw.thumbnail) ||
        (typeof raw.thumbUrl === 'string' && raw.thumbUrl) ||
        '';
      selected = {
        name: raw.name || raw.title || 'Untitled',
        image,
        imageThumb,
        tags: Array.isArray(raw.tags) ? raw.tags : [],
        serves: Number(raw.servings) || Number(raw.serves) || 1,
        baseServings: Number(raw.servings) || Number(raw.serves) || 1,
        mealType: String(raw.mealType || '').toLowerCase(),
        method: detail.method || [],
        unitSystem: detail.unitSystem || 'metric',
        ingredientsRaw: Array.isArray(raw.ingredients) ? raw.ingredients : [],
      };
      selected.servesPreferred = getPreferredServes(selected.mealType, selected.serves);
    } catch (e) {
      console.error('Failed to load selected recipe:', e);
      setPageError("We couldn't load that recipe just now. Please try again.");
      return;
    }

    // Update the meal in the weeksData state
    setWeeksData(prevWeeks => {
      const newWeeks = [...prevWeeks];
      const weekToUpdate = { ...newWeeks[currentWeekIndex] };

      // Find which day the meal is in
      let foundDay = null;
      for (const day of Object.keys(weekToUpdate.days)) {
        if (weekToUpdate.days[day].some(m => m.id === mealToSwap.id)) {
          foundDay = day;
          break;
        }
      }

      if (foundDay) {
        const dayMeals = [...(weekToUpdate.days[foundDay] || [])];
        const mealIndex = dayMeals.findIndex(m => m.id === mealToSwap.id);
        if (mealIndex !== -1) {
          dayMeals[mealIndex] = {
            ...dayMeals[mealIndex],
            name: selected.name,
            image: selected.imageThumb || selected.image,
            tags: selected.tags,
            serves: selected.servesPreferred,
            recipeId: recipeId,
            isLocalSwap: true,
          };
        }
        weekToUpdate.days = {
          ...weekToUpdate.days,
          [foundDay]: dayMeals,
        };
      }

      newWeeks[currentWeekIndex] = weekToUpdate;
      return newWeeks;
    });

    setSwapMealModalOpen(false);
    setMealToSwap(null);

    // Persist swap to weeklyPlans and regenerate shopping list
    const weeklyPlanId = mealToSwap.id;
  const targetServes = Number(selected.servesPreferred) || selected.baseServings;
    const isId = isObjectId(weeklyPlanId);
    if (isId) {
      // Build baseline ingredients with numeric quantities
      const parseQtyToNumber = (value) => {
        if (typeof value === 'number') return isNaN(value) ? null : value;
        if (typeof value !== 'string') return null;
        const s = value.trim();
        if (!s) return null;
        const parts = s.split(' ');
        let total = 0;
        for (const p of parts) {
          if (!p) continue;
          if (/^[0-9]+\/[0-9]+$/.test(p)) {
            const [n, d] = p.split('/').map(Number);
            if (d === 0 || isNaN(n) || isNaN(d)) return null;
            total += n / d;
          } else if (/^[0-9]+(\.[0-9]+)?$/.test(p)) {
            const n = Number(p);
            if (isNaN(n)) return null;
            total += n;
          } else {
            break;
          }
        }
        if (total === 0) {
          const n = Number(s);
          if (!isNaN(n)) return n;
        }
        return Math.round(total * 100) / 100;
      };

      const baseIngredients = selected.ingredientsRaw.map(ing => {
        const name = ing.name || ing.ingredient || '';
        const unit = ing.unit || ing.units || '';
        const q = ing.quantity;
        const num = typeof q === 'number' ? q : parseQtyToNumber(q);
        return {
          name,
          quantity: typeof num === 'number' && !isNaN(num) ? num : 0,
          unit,
          category: ing.category || undefined,
        };
      });

      try {
        // Step 1: update recipe fields and baseline on weekly plan doc
        await apiPatch(`/weeklyPlans/${encodeURIComponent(weeklyPlanId)}`, {
          recipeId: String(recipeId),
          name: selected.name,
          // Write the image group as a unit. Sending `image` alone left `imagePath` pointing at
          // whichever recipe previously held this slot, and hydration re-signs from the path —
          // so the slot rendered the old recipe's photo.
          image: selected.image,
          imagePath: selected.imagePath || '',
          imageThumb: selected.imageThumb,
          imageThumbPath: selected.imageThumbPath || '',
          tags: selected.tags,
          mealType: selected.mealType,
          baseServings: selected.baseServings,
          baseIngredients,
          unitSystem: selected.unitSystem || 'metric',
          method: selected.method,
          instructions: selected.method,
        });
        // Step 2: recompute ingredients for current serves from new baseline
        await apiPatch(`/weeklyPlans/${encodeURIComponent(weeklyPlanId)}`, {
          serves: targetServes,
        });
        // Step 3: regenerate shopping list for this week, preserving user deltas
        await regenerateShoppingListForCurrentWeek();
      } catch (e) {
        console.warn('Failed to persist swap or regenerate list:', e);
        setPageError("We couldn't save the swap. Please try again.");
      }
    }
  };

  // Swap Modal: auto-generate placeholder (non-blocking UI)
  const handleAutoGenerateMeal = () => {
    console.log(`Auto generate new ${mealToSwap?.mealType} meal for ${mealToSwap?.id}`);
    // In production: call API to generate a new suggestion for this slot
    setSwapMealModalOpen(false);
    setMealToSwap(null);
  };

  const navigate = useNavigate();
  const location = useLocation();
  // Holds recipe details (ingredients + servings) for the currently viewed week
  const [, setWeekRecipeDetailsMap] = useState({});
  // Current view's week start date (YYYY-MM-DD) for backend shopping list
  const currentWeekStartISO = useMemo(() => {
    const weeksOffset = currentWeekIndex - actualCurrentWeekIndex;
    const base = new Date();
    base.setDate(base.getDate() + weeksOffset * 7);
    return getWeekStartISO(base, weekStartDow);
  }, [currentWeekIndex, actualCurrentWeekIndex, weekStartDow]);
  // Persist the viewed week so Shopping List opens on the same week (and vice versa).
  useEffect(() => {
    writeViewWeekOffset(currentWeekIndex - actualCurrentWeekIndex);
  }, [currentWeekIndex, actualCurrentWeekIndex]);
  // User preference serves per meal type (fallbacks if not loaded)
  const [defaultServesPrefs, setDefaultServesPrefs] = useState({ breakfast: 2, lunch: 2, dinner: 4 });
  const [includedMealsPrefs, setIncludedMealsPrefs] = useState({ breakfast: true, lunch: true, dinner: true });

  const getPreferredServes = (mealType, fallback) => {
    const key = String(mealType || '').toLowerCase();
    const val = defaultServesPrefs[key];
    const n = Number(val);
    if (!isNaN(n) && n > 0) return n;
    const fb = Number(fallback);
    return !isNaN(fb) && fb > 0 ? fb : 1;
  };

  const regenerateShoppingListForCurrentWeek = () =>
    apiPost(`/generate-shopping-list/week-start/${encodeURIComponent(currentWeekStartISO)}`);

  const showSuccessBanner = (message) => {
    setSuccessBannerMessage(message);
    if (successBannerTimerRef.current) {
      window.clearTimeout(successBannerTimerRef.current);
    }
    successBannerTimerRef.current = window.setTimeout(() => {
      setSuccessBannerMessage('');
      successBannerTimerRef.current = null;
    }, 7000);
  };

  // Recipe edit / delete / add-to-plan / replace actions + their drawer state.
  const {
    editingRecipe,
    setEditingRecipe,
    deletingRecipeId,
    setDeletingRecipeId,
    recipeToAddToPlan,
    setRecipeToAddToPlan,
    replaceMealData,
    setReplaceMealData,
    handleEditRecipe,
    handleSaveEditedRecipe,
    handleDeleteRecipe,
    handleConfirmDeleteRecipe,
    handleConfirmAddToMealPlan,
    handleConfirmReplaceMeal,
  } = useRecipeActions({
    weeksData,
    setWeeksData,
    setCurrentWeekIndex,
    setSelectedDay,
    actualCurrentWeekIndex,
    weekStartDow,
    getPreferredServes,
    setViewingMealRecipe,
    showSuccessBanner,
  });

  // Add-to-plan handed off from the standalone Recipes route: open the add-to-plan drawer here
  // (this page has the real weeksData + conflict detection), then clear the nav state so it
  // doesn't retrigger on back/refresh.
  useEffect(() => {
    const handoff = location.state?.addRecipe;
    if (handoff) {
      setRecipeToAddToPlan(handoff);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate, setRecipeToAddToPlan]);

  // Load weekStartDay preference so week calculations align with user setting. MealPlan only
  // mounts on the /MealPlan route now, so this runs on mount — returning from the More route
  // (where Preferences is edited) remounts MealPlan and re-reads prefs without a full reload.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const prefs = await apiGet('/user/preferences');
        const raw = String(prefs?.weekStartDay || 'monday').toLowerCase();
        const map = {
          sunday: 0,
          monday: 1,
          tuesday: 2,
          wednesday: 3,
          thursday: 4,
          friday: 5,
          saturday: 6,
        };
        const dow = map[raw] ?? 1;
        if (!cancelled) {
          setWeekStartDow(dow);
          try { localStorage.setItem('mp:weekStartDow', String(dow)); } catch { /* ignore */ }
        }
        if (!cancelled && prefs?.includedMeals) {
          setIncludedMealsPrefs((prev) => ({
            breakfast: typeof prefs.includedMeals.breakfast === 'boolean' ? prefs.includedMeals.breakfast : prev.breakfast,
            lunch: typeof prefs.includedMeals.lunch === 'boolean' ? prefs.includedMeals.lunch : prev.lunch,
            dinner: typeof prefs.includedMeals.dinner === 'boolean' ? prefs.includedMeals.dinner : prev.dinner,
          }));
        }
        if (!cancelled && prefs?.defaultServes) {
          setDefaultServesPrefs((prev) => ({
            breakfast: Number(prefs.defaultServes.breakfast) || prev.breakfast || 2,
            lunch: Number(prefs.defaultServes.lunch) || prev.lunch || 2,
            dinner: Number(prefs.defaultServes.dinner) || prev.dinner || 4,
          }));
        }
      } catch (e) {
        console.warn('Failed to load weekStartDay preference, defaulting to Monday:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Responsive screen detection
useEffect(() => {
  const mediaQuery = window.matchMedia('(min-width: 768px)');
  
  const handleMediaChange = (e) => {
    setIsLargeScreen(e.matches);
  };
  
  // Set initial value
  setIsLargeScreen(mediaQuery.matches);
  
  // Listen for changes
  mediaQuery.addEventListener('change', handleMediaChange);
  
  // Cleanup
  return () => mediaQuery.removeEventListener('change', handleMediaChange);
}, []);

  useEffect(() => {
    return () => {
      if (successBannerTimerRef.current) {
        window.clearTimeout(successBannerTimerRef.current);
      }
    };
  }, []);

  const renderSuccessBanner = () => {
    if (!successBannerMessage) return null;
    return (
      <div
        className="mx-auto mb-3 rounded-xl border border-[#BFDCCB] bg-[#EAF6EE] px-3 py-2 text-sm text-[#1F5D38]"
        style={{
          marginLeft: isLargeScreen ? '3rem' : '1.5rem',
          marginRight: isLargeScreen ? '3rem' : '1.5rem',
        }}
        role="status"
        aria-live="polite"
      >
        <div className="flex items-start gap-2">
          <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#1F5D38]" />
          <span>{successBannerMessage}</span>
        </div>
      </div>
    );
  };

  const renderErrorBanner = () => (
    // Always mounted so a new error is announced.
    <div role="alert">
      {pageError && (
        <div
          className="mx-auto my-3 flex items-start gap-2 rounded-xl border border-[#D4183D] bg-[#FEF3F2] py-1 pl-3 pr-1 text-sm text-[#B42318]"
          style={{
            marginLeft: isLargeScreen ? '3rem' : '1.5rem',
            marginRight: isLargeScreen ? '3rem' : '1.5rem',
          }}
        >
          <p className="flex-1 py-2">{pageError}</p>
          <button
            type="button"
            onClick={() => setPageError('')}
            aria-label="Dismiss message"
            className="w-11 h-11 flex-shrink-0 rounded-full flex items-center justify-center hover:bg-[#FDE2E0]"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );

  // Show Full Week View if active
  if (isFullWeekView) {
    const weekStartDateObj = new Date(`${currentWeekStartISO}T00:00:00`);
    const fullWeekLabel = formatWeekLabel(weekStartDateObj);
    return (
      <>
        {renderErrorBanner()}
        <FullWeekView
          weekData={currentWeek}
          daysOrder={days}
          weekLabel={fullWeekLabel}
          onBack={() => setIsFullWeekView(false)}
          onViewMealDetails={handleViewDetails}
          onSwap={handleSwap}
          onRemove={(id) => {
            // Find the meal's day and remove it
            const allDays = Object.keys(currentWeek.days);
            for (const day of allDays) {
              const dayMeals = currentWeek.days[day];
              const mealIndex = dayMeals.findIndex(m => m.id === id);
              if (mealIndex !== -1) {
                // Set the selected day so handleRemove works correctly
                setSelectedDay(day);
                // Wait a tick for state to update, then remove
                setTimeout(() => handleRemove(id), 0);
                break;
              }
            }
          }}
        />

        {/* Swap Meal Modal */}
        {mealToSwap && (
          <SwapMealModal
            isOpen={swapMealModalOpen}
            onClose={() => {
              setSwapMealModalOpen(false);
              setMealToSwap(null);
            }}
            mealType={mealToSwap.mealType}
            currentMealId={mealToSwap.id}
            currentRecipeId={mealToSwap.recipeId}
            currentMealName={mealToSwap.name}
            onSwap={handleSwapMeal}
            onAutoGenerate={handleAutoGenerateMeal}
          />
        )}
      </>
      );
  }
      // If viewing a meal recipe detail, show that instead
      if (viewingMealRecipe) {
    // Clear the meal recipe view, then navigate to the tapped tab's route (meal-plan just
    // returns to the grid since we're already on /MealPlan).
    const handleTabChangeFromRecipeDetail = (tab) => {
      setViewingMealRecipe(null);
      if (tab === 'shopping-list') navigate('/ShoppingList');
      else if (tab === 'recipes') navigate('/AllRecipes');
      else if (tab === 'more') navigate('/More');
    };

      return (
      <>
        {renderErrorBanner()}
        {renderSuccessBanner()}
        <RecipeDetail
          recipe={viewingMealRecipe}
          onBack={() => setViewingMealRecipe(null)}
          activeTab="meal-plan"
          onTabChange={handleTabChangeFromRecipeDetail}
          onServesChange={handleRecipeServesChange}
          onEdit={handleEditRecipe}
          onDelete={handleDeleteRecipe}
          successBannerMessage={successBannerMessage}
          isLargeScreen={isLargeScreen}
        />

        {/* Global Modals */}
        <ManualRecipeForm
          isOpen={!!editingRecipe}
          onClose={() => setEditingRecipe(null)}
          onSave={handleSaveEditedRecipe}
          existingRecipe={editingRecipe}
          userId={userId}
        />

        <ConfirmationDrawer
          isOpen={!!deletingRecipeId}
          onCancel={() => setDeletingRecipeId(null)}
          onConfirm={handleConfirmDeleteRecipe}
          title="Delete Recipe?"
          message="This recipe will be permanently deleted from your collection. This action cannot be undone."
        />
      </>
      );
  }

      // Render content based on active tab
      return (
      <div className="min-h-screen bg-[#F8F7F5] flex justify-center">
        {/* Mobile Container - NOW RESPONSIVE */}
      <div 
        className="w-full bg-[#F8F7F5] min-h-screen relative pb-24"
        style={{ 
          maxWidth: isLargeScreen ? '1200px' : '448px',
          transition: 'max-width 0.3s ease'
        }}
      >
          {/* Status Bar Space */}
          <div className="h-8" />

          {/* Header */}
          <header className="py-3"        
              
                style={{ 
                paddingLeft: isLargeScreen ? '3rem' : '1.5rem',
                paddingRight: isLargeScreen ? '3rem' : '1.5rem'
              }}
          >
            <div className="flex items-center justify-between">
              <div class="w-10"></div>
              <h1 
              style={{ 
                fontSize: isLargeScreen ? '40px' : '28px',
                transition: 'font-size 0.3s ease'
              }}
            >
              Meal Plan
            </h1>
              <WeekMenu
                 onDeleteWeek={handleDeleteWeek}
                 onViewFullWeek={handleViewFullWeek}
                 onRegenerateWeek={handleGenerateWeek}
                 hasAnyMeals={weekHasAnyMeals()}
               />
            </div>
          </header>

          {renderErrorBanner()}
          {renderSuccessBanner()}

          {/* Main Content */}
          <main 
          className="pb-8"
          style={{ 
            paddingLeft: isLargeScreen ? '3rem' : '1.5rem',
            paddingRight: isLargeScreen ? '3rem' : '1.5rem'
          }}
        >
            {/* Week Navigation */}
            <section className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <button
                  className={`w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm transition-colors ${currentWeekIndex === 0
                      ? 'opacity-40 cursor-not-allowed'
                      : 'hover:bg-gray-50'
                    }`}
                  onClick={handlePreviousWeek}
                  disabled={currentWeekIndex === 0}
                >
                  <ChevronLeft className={`w-5 h-5 ${currentWeekIndex === 0 ? 'text-gray-400' : 'text-gray-700'}`} />
                </button>

                <div className="flex flex-col items-center gap-1">
                  <h3 className="text-base font-medium">{formatWeekLabel(new Date(`${currentWeekStartISO}T00:00:00Z`))}</h3>
                  {currentWeekIndex === actualCurrentWeekIndex && (
                    <span
                      className="px-3 py-0.5 rounded-full text-xs"
                      style={{ backgroundColor: 'var(--accent)', color: 'var(--accent-foreground)' }}
                    >
                      This Week
                    </span>
                  )}
                </div>

                <button
                  className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm hover:bg-gray-50 transition-colors"
                  onClick={handleNextWeek}
                >
                  <ChevronRight className="w-5 h-5 text-gray-700" />
                </button>
              </div>

              {/* Loading banner removed; hydration still gates the Generate button */}

              {/* Generate Week Button - only show for empty weeks that are current week or future */}
              {!isLoadingWeek
                && hydratedWeeks[currentWeekIndex] === true
                && !weekHasAnyMeals()
                && currentWeekIndex >= actualCurrentWeekIndex && (
                 <button
                   onClick={handleGenerateWeek}
                   disabled={isGenerating}
                    className="w-full py-3 rounded-full shadow-md transition-colors mb-4 flex items-center justify-center gap-2 btn-accent"
                 >
                   <Sparkles className="w-5 h-5" />
                   {isGenerating ? 'Generating…' : "Generate This Week's Plan"}
                 </button>
               )}

              {/* Day Selector Pills */}
              <div className="bg-white rounded-full p-1 shadow-sm flex items-center justify-between">
                {days.map((day) => {
                  const isToday = isCurrentWeek && day === todayShortName;
                  return (
                    <button
                      key={day}
                      onClick={() => setSelectedDay(day)}
                      className={`flex-1 py-2 rounded-full transition-all relative ${selectedDay === day
                          ? 'shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                        }`}
                      style={selectedDay === day
                        ? { backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }
                        : undefined}
                    >
                      {day}
                      {isToday && (
                        <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 h-0.5" style={{ width: '80%', backgroundColor: 'currentColor' }} />
                      )}
                    </button>
                );
                })}
              </div>
            </section>

            {/* Selected Day Meals */}
            <section className="mb-8">
              <div className="space-y-3">
                {/* Always show all three meal types */}
                {[
                  { label: 'Breakfast', meal: mealsByType.Breakfast, included: includedMealsPrefs.breakfast },
                  { label: 'Lunch', meal: mealsByType.Lunch, included: includedMealsPrefs.lunch },
                  { label: 'Dinner', meal: mealsByType.Dinner, included: includedMealsPrefs.dinner },
                ].map(({ label, meal, included }) => {
                  const isCompactEmpty = !meal && included === false;
                  return (
                    <div key={label}>
                      {!isCompactEmpty && <p className="text-gray-500 mb-2 px-1">{label}</p>}
                      {meal ? (
                        <MealCard
                          id={meal.id}
                          name={meal.name}
                          image={meal.image}
                          serves={meal.serves}
                          onServesChange={handleServesChange}
                          onSwap={handleSwap}
                          onRemove={handleRemove}
                          onViewDetails={handleViewDetails}
                        />
                      ) : (
                        <EmptyMealSlot mealType={label} onAdd={handleAddMeal} compact={isCompactEmpty} />
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Generate Button - removed from here */}
          </main>

          {/* Bottom Navigation */}
          <BottomNav />
        </div>

        {/* Swap Meal Modal */}
        {mealToSwap && (
          <SwapMealModal
            isOpen={swapMealModalOpen}
            onClose={() => {
              setSwapMealModalOpen(false);
              setMealToSwap(null);
            }}
            mealType={mealToSwap.mealType}
            currentMealId={mealToSwap.id}
            currentRecipeId={mealToSwap.recipeId}
            currentMealName={mealToSwap.name}
            onSwap={handleSwapMeal}
            onAutoGenerate={handleAutoGenerateMeal}
          />
        )}

        {/* Add Meal Modal */}
        {addMealModalOpen && selectedMealType && (
          <AddMealModal
            isOpen={addMealModalOpen}
            onClose={() => {
              setAddMealModalOpen(false);
              setSelectedMealType(null);
            }}
            mealType={selectedMealType}
            onAdd={handleAddMealFromModal}
            onAutoGenerate={handleAutoGenerateAddMeal}
          />
        )}

        {/* Edit Recipe Form */}
        <ManualRecipeForm
          isOpen={!!editingRecipe}
          onClose={() => setEditingRecipe(null)}
          onSave={handleSaveEditedRecipe}
          existingRecipe={editingRecipe}
          userId={userId}
        />

        {/* Delete Recipe Confirmation */}
        <ConfirmationDrawer
          isOpen={!!deletingRecipeId}
          onCancel={() => setDeletingRecipeId(null)}
          onConfirm={handleConfirmDeleteRecipe}
          title="Delete Recipe?"
          message="This recipe will be permanently deleted from your collection. This action cannot be undone."
        />

        <AddToMealPlanDrawer
          isOpen={!!recipeToAddToPlan}
          onClose={() => setRecipeToAddToPlan(null)}
          onConfirm={handleConfirmAddToMealPlan}
          recipe={recipeToAddToPlan}
          currentWeekIndex={currentWeekIndex}
          weeksData={weeksData}
          weekStartDow={weekStartDow}
          actualCurrentWeekIndex={actualCurrentWeekIndex}
        />

        <ReplaceMealConfirmDrawer
          isOpen={!!replaceMealData}
          onClose={() => setReplaceMealData(null)}
          onConfirm={handleConfirmReplaceMeal}
          existingMealName={replaceMealData?.existingMeal.name || ''}
          newMealName={replaceMealData?.newRecipe.name || ''}
          day={replaceMealData?.day || ''}
          mealType={replaceMealData?.newRecipe.mealType || ''}
        />

      </div>
      );
}